import { ALL_WATCHTOWER_TYPES, ERROR_CODE, WATCHTOWER_TYPE, parse } from "@packages/core";
import { useForm, useStore } from "@tanstack/react-form";
import { Link, createFileRoute, useBlocker, useNavigate } from "@tanstack/react-router";
import React, { Fragment, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Merge } from "type-fest";
import * as v from "valibot";

import {
  Button,
  DropDownList,
  FormField,
  FormFieldInput,
  FormFieldInputStyle,
  PageName,
  buttonStyles,
  showError,
} from "#components";
import {
  $alert,
  $confirm,
  registerWatchtower,
  removeWatchtowersCache,
  useGetErrorMessage,
  useTranslate,
} from "#services";
import { css, cx } from "#styled-system/css";

export const Route = createFileRoute("/_authenticated/management/watchtowers/registration")({
  component: Component,
});

function Component() {
  const { t } = useTranslation();
  const [type, setType] = useState(WATCHTOWER_TYPE.SLACK_INCOMING_WEBHOOK);

  return (
    <main>
      <PageName>{t("%WatchtowerRegistration.0%")}</PageName>
      <p className={css({ fontSize: "24px", fontWeight: 700 })}>
        {t("%WatchtowerRegistration.0%")}
      </p>

      <DropDownList
        className={css({ backgroundColor: "white", marginTop: "16px", maxWidth: "320px" })}
        onChange={function (event) {
          setType(event.target.value);
        }}
        value={type}
      >
        {ALL_WATCHTOWER_TYPES.map(function (type) {
          return (
            <option key={type} value={type}>
              {t("%WatchtowerType." + type + ".0%")}
            </option>
          );
        })}
      </DropDownList>

      {type === WATCHTOWER_TYPE.SLACK_INCOMING_WEBHOOK && <SlackIncomingWebhookForm />}
      {type === WATCHTOWER_TYPE.EMAIL_ADDRESS && <EmailAddressForm />}
    </main>
  );
}

type EmailAddressFormProperties = Merge<
  React.ComponentProps<"form">,
  { children?: never; onSubmit?: never }
>;
function EmailAddressForm({ className, ...properties }: EmailAddressFormProperties) {
  const getErrorMessage = useGetErrorMessage();
  const navigate = useNavigate();
  const translate = useTranslate();

  const formSchema = useMemo(function () {
    return v.object({
      /** 名前 */
      name: v.pipe(
        v.string(ERROR_CODE.INPUT_REQUIRED),
        v.minLength(1, ERROR_CODE.INPUT_REQUIRED),
        v.maxGraphemes(128, ERROR_CODE.TOO_MANY_CHARACTERS),
      ),
      emailAddress: v.pipe(
        v.string(ERROR_CODE.INPUT_REQUIRED),
        v.email(ERROR_CODE.INVALID_EMAIL_ADDRESS_INPUT),
      ),
    });
  }, []);
  const defaultValues = useMemo(function (): v.InferInput<typeof formSchema> {
    return { emailAddress: "", name: "" };
  }, []);

  const form = useForm({
    defaultValues,
    async onSubmit({ value: input }) {
      const output = parse(formSchema, input);
      console.debug("フォームが送信されました。", output);

      let watchtower;
      try {
        const response = await registerWatchtower({
          type: WATCHTOWER_TYPE.EMAIL_ADDRESS,
          ...output,
        });
        watchtower = response.watchtower;
      } catch (error) {
        showError(error);
        return;
      }

      await $alert({
        color: "success",
        content: translate("%Register.2%", { $: { name: watchtower.name } }),
        title: translate("%Register.0%"),
      });

      removeWatchtowersCache();

      await navigate({ to: "/management/watchtowers", ignoreBlocker: true });
    },
    validators: { onSubmit: formSchema },
  });

  {
    const isDefaultValue = useStore(form.store, (state) => state.isDefaultValue);
    const isSubmitted = useStore(form.store, (state) => state.isSubmitted);
    const isSubmitting = useStore(form.store, (state) => state.isSubmitting);
    useBlocker({
      async shouldBlockFn() {
        return (!isDefaultValue && !isSubmitted) || isSubmitting
          ? !(await $confirm({
              color: "warning",
              content: translate("%Form.1%"),
              no: translate("%Stay.0%"),
              title: translate("%Form.0%"),
              yes: translate("%Leave.0%"),
            }))
          : false;
      },
      withResolver: true,
    });
  }

  return (
    <form
      {...properties}
      className={cx(
        css({ display: "flex", flexDirection: "column", gap: "16px", marginTop: "16px" }),
        className,
      )}
      onSubmit={function (event) {
        event.preventDefault();
        event.stopPropagation();
        form.handleSubmit();
      }}
    >
      <form.Field
        children={function (field) {
          return (
            <FormField
              caution={field.state.meta.errors
                .map(function (error) {
                  return getErrorMessage(error);
                })
                .join("")}
              className={css({ marginTop: "16px", width: "100%" })}
              invalid={field.state.meta.errors.length > 0}
              label={translate("%Name.0%")}
            >
              <FormFieldInput
                className={FormFieldInputStyle({ width: "50%" })}
                id={field.name}
                onBlur={function () {
                  field.handleBlur();
                }}
                onChange={function (event) {
                  field.handleChange(event.target.value as any);
                }}
                value={field.state.value}
              />
            </FormField>
          );
        }}
        name="name"
      />
      <form.Field
        children={function (field) {
          return (
            <FormField
              caution={field.state.meta.errors
                .map(function (error) {
                  return getErrorMessage(error);
                })
                .join("")}
              className={css({ marginTop: "16px", width: "100%" })}
              invalid={field.state.meta.errors.length > 0}
              label={translate("%EmailAddress.0%")}
            >
              <FormFieldInput
                className={FormFieldInputStyle({ width: "50%" })}
                id={field.name}
                onBlur={function () {
                  field.handleBlur();
                }}
                onChange={function (event) {
                  field.handleChange(event.target.value as any);
                }}
                value={field.state.value}
              />
            </FormField>
          );
        }}
        name="emailAddress"
      />

      <div className={css({ display: "flex", gap: "8px" })}>
        <form.Subscribe
          selector={function ({ isSubmitting }) {
            return isSubmitting;
          }}
        >
          {function (submitting) {
            return (
              <Fragment>
                <Link
                  className={buttonStyles({ color: "secondary" })}
                  disabled={submitting}
                  to="/management/watchtowers"
                >
                  {translate("%Cancel.0%")}
                </Link>
                <Button
                  className={css({ width: "max-content" })}
                  disabled={submitting}
                  type="submit"
                >
                  {translate("%Save.0%")}
                </Button>
              </Fragment>
            );
          }}
        </form.Subscribe>
      </div>
    </form>
  );
}

type SlackIncomingWebhookFormProperties = Merge<
  React.ComponentProps<"form">,
  { children?: never; onSubmit?: never }
>;
function SlackIncomingWebhookForm({
  className,
  ...properties
}: SlackIncomingWebhookFormProperties) {
  const getErrorMessage = useGetErrorMessage();
  const navigate = useNavigate();
  const translate = useTranslate();

  const formSchema = useMemo(function () {
    return v.object({
      /** 名前 */
      name: v.pipe(
        v.string(ERROR_CODE.INPUT_REQUIRED),
        v.minLength(1, ERROR_CODE.INPUT_REQUIRED),
        v.maxGraphemes(128, ERROR_CODE.TOO_MANY_CHARACTERS),
      ),
      url: v.pipe(v.string(ERROR_CODE.INPUT_REQUIRED), v.url(ERROR_CODE.INVALID_HTTPS_URL)),
    });
  }, []);
  const defaultValues = useMemo(function (): v.InferInput<typeof formSchema> {
    return { name: "", url: "" };
  }, []);

  const form = useForm({
    defaultValues,
    async onSubmit({ value: input }) {
      const output = parse(formSchema, input);
      console.debug("フォームが送信されました。", output);

      let watchtower;
      try {
        const response = await registerWatchtower({
          type: WATCHTOWER_TYPE.SLACK_INCOMING_WEBHOOK,
          ...output,
        });
        watchtower = response.watchtower;
      } catch (error) {
        showError(error);
        return;
      }

      await $alert({
        color: "success",
        content: translate("%Register.2%", { $: { name: watchtower.name } }),
        title: translate("%Register.0%"),
      });

      removeWatchtowersCache();

      await navigate({ to: "/management/watchtowers", ignoreBlocker: true });
    },
    validators: { onSubmit: formSchema },
  });

  {
    const isDefaultValue = useStore(form.store, (state) => state.isDefaultValue);
    const isSubmitted = useStore(form.store, (state) => state.isSubmitted);
    const isSubmitting = useStore(form.store, (state) => state.isSubmitting);
    useBlocker({
      async shouldBlockFn() {
        return (!isDefaultValue && !isSubmitted) || isSubmitting
          ? !(await $confirm({
              color: "warning",
              content: translate("%Form.1%"),
              no: translate("%Stay.0%"),
              title: translate("%Form.0%"),
              yes: translate("%Leave.0%"),
            }))
          : false;
      },
      withResolver: true,
    });
  }

  return (
    <form
      {...properties}
      className={cx(
        css({ display: "flex", flexDirection: "column", gap: "16px", marginTop: "16px" }),
        className,
      )}
      onSubmit={function (event) {
        event.preventDefault();
        event.stopPropagation();
        form.handleSubmit();
      }}
    >
      <form.Field
        children={function (field) {
          return (
            <FormField
              caution={field.state.meta.errors
                .map(function (error) {
                  return getErrorMessage(error);
                })
                .join("")}
              className={css({ marginTop: "16px", width: "100%" })}
              invalid={field.state.meta.errors.length > 0}
              label={translate("%Name.0%")}
            >
              <FormFieldInput
                className={FormFieldInputStyle({ width: "50%" })}
                id={field.name}
                onBlur={function () {
                  field.handleBlur();
                }}
                onChange={function (event) {
                  field.handleChange(event.target.value as any);
                }}
                value={field.state.value}
              />
            </FormField>
          );
        }}
        name="name"
      />
      <form.Field
        children={function (field) {
          return (
            <FormField
              caution={field.state.meta.errors
                .map(function (error) {
                  return getErrorMessage(error);
                })
                .join("")}
              className={css({ marginTop: "16px", width: "100%" })}
              invalid={field.state.meta.errors.length > 0}
              label="Slack Incoming Webhook URL"
            >
              <FormFieldInput
                className={FormFieldInputStyle({ width: "50%" })}
                id={field.name}
                onBlur={function () {
                  field.handleBlur();
                }}
                onChange={function (event) {
                  field.handleChange(event.target.value as any);
                }}
                value={field.state.value}
              />
            </FormField>
          );
        }}
        name="url"
      />

      <div className={css({ display: "flex", gap: "8px" })}>
        <form.Subscribe
          selector={function ({ isSubmitting }) {
            return isSubmitting;
          }}
        >
          {function (submitting) {
            return (
              <Fragment>
                <Link
                  className={buttonStyles({ color: "secondary" })}
                  disabled={submitting}
                  to="/management/watchtowers"
                >
                  {translate("%Cancel.0%")}
                </Link>
                <Button
                  className={css({ width: "max-content" })}
                  disabled={submitting}
                  type="submit"
                >
                  {translate("%Save.0%")}
                </Button>
              </Fragment>
            );
          }}
        </form.Subscribe>
      </div>
    </form>
  );
}
